import { z } from "zod";
import type { ScimUserProfile } from "../types/organization";

// Flat, render-only profile (amended by TECH-2964). The shape stored on
// `User.profile` and served to the client. `email` and `firebaseUid` live on
// the User, not here. `displayName` is **always present**, the always-safe
// label derived by `scimToProfile`. Name parts and photo are optional, and each
// adapter fills only the subset its upstream offers.
export const ProfileSchema = z.object({
	givenName: z.string().nullish(),
	familyName: z.string().nullish(),
	displayName: z.string(),
	profilePhoto: z.string().nullish(),
});
export type Profile = z.infer<typeof ProfileSchema>;

// The self-contained user record served over the WS, replacing the old
// StrapiUserProfile on the wire. Flat, so the client stores it directly as a
// user entry's `.profile`. Keyed client-side by `firebaseUid`. `displayName`
// rides inside the Profile, so the client never re-derives it.
export const UserProfileSchema = ProfileSchema.extend({
	firebaseUid: z.string(),
	email: z.string(),
	organization: z.object({ id: z.string(), name: z.string() }),
});
export type UserProfile = z.infer<typeof UserProfileSchema>;

// The neutral `displayName` produced when an upstream record carries no usable
// name at all, meaning no preferred name and no given or family name. The
// import treats a profile that resolves to this, with no name parts, as too
// incomplete to import, because it would render as this placeholder. Exported
// for that check.
export const UNKNOWN_DISPLAY_NAME = "Unknown User";

// Shared SCIM to flat normalizer (amended by TECH-2964). Adapters emit the
// SCIM-shaped contract, and the import flow runs this once to land the flat
// Profile. This is the one place `displayName` is derived, from the boundary's
// preferred name (carried in SCIM `displayName`), degrading to the joined name
// parts and then to a neutral label. It never falls back to email, which must
// not be shown in /app. `active` is part of the SCIM contract but not consumed,
// because roster presence drives participation.
export function scimToProfile(scim: ScimUserProfile): Profile {
	const photo =
		scim.photos?.find((p) => p.type === "photo")?.value ??
		scim.photos?.[0]?.value ??
		null;
	const joined = [scim.name?.givenName, scim.name?.familyName]
		.filter(Boolean)
		.join(" ")
		.trim();
	return {
		givenName: scim.name?.givenName ?? null,
		familyName: scim.name?.familyName ?? null,
		displayName: scim.displayName?.trim() || joined || UNKNOWN_DISPLAY_NAME,
		profilePhoto: photo,
	};
}
